// Direct printing over WebUSB / Web Bluetooth. Device config is per browser (localStorage).
export const PRINTER_KEY = "samrtin_printer";
export const UNSUPPORTED_COPY = "Direct printing needs Chrome or Edge — using browser print";

export const supports = {
  usb: () => typeof navigator !== "undefined" && !!navigator.usb,
  bluetooth: () => typeof navigator !== "undefined" && !!navigator.bluetooth,
};

// Common thermal printer vendors (Epson, Star, Citizen, Bixolon, SNBC, Rongta/Gprinter, Xprinter/Zjiang/Winbond, HPRT, STM, GD32, Nuvoton).
const USB_VENDORS = [0x04b8, 0x0519, 0x1d90, 0x1504, 0x154f, 0x0fe6, 0x0416, 0x20d1, 0x0483, 0x28e9, 0x0471, 0x1fc9, 0x6868, 0x0dd4, 0x1659];

// GATT services used by Bluetooth LE thermal printers (generic 18F0, Chinese "e781…", ISSC/Microchip, HM-10 FFE0, FF00, Nordic UART, AE30).
const BT_SERVICES = [
  "000018f0-0000-1000-8000-00805f9b34fb",
  "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
  "49535343-fe7d-4ae5-8fa9-9fafd205e455",
  "0000ffe0-0000-1000-8000-00805f9b34fb",
  "0000ff00-0000-1000-8000-00805f9b34fb",
  "0000fee7-0000-1000-8000-00805f9b34fb",
  "0000ae30-0000-1000-8000-00805f9b34fb",
  "6e400001-b5a3-f393-e0a9-e50e24dcca9e",
];

export const loadPrinter = () => {
  try {
    return JSON.parse(localStorage.getItem(PRINTER_KEY)) || { mode: "browser" };
  } catch {
    return { mode: "browser" };
  }
};
export const savePrinter = (cfg) => localStorage.setItem(PRINTER_KEY, JSON.stringify(cfg));

const live = { usb: null, bt: null, btChar: null };

// ---------- USB ----------
export async function connectUsb() {
  let device;
  try {
    device = await navigator.usb.requestDevice({ filters: [] });
  } catch (e) {
    if (e?.name === "NotFoundError") throw e;
    device = await navigator.usb.requestDevice({ filters: USB_VENDORS.map((vendorId) => ({ vendorId })) });
  }
  live.usb = device;
  return { vendorId: device.vendorId, productId: device.productId, serialNumber: device.serialNumber || null, name: device.productName || `USB ${device.vendorId.toString(16)}:${device.productId.toString(16)}` };
}

async function usbDevice(info) {
  if (live.usb) return live.usb;
  const devices = await navigator.usb.getDevices();
  const d = devices.find((x) => x.vendorId === info?.vendorId && x.productId === info?.productId && (!info.serialNumber || x.serialNumber === info.serialNumber));
  if (!d) throw new Error("USB printer not paired");
  live.usb = d;
  return d;
}

async function usbSend(info, bytes) {
  const d = await usbDevice(info);
  if (!d.opened) await d.open();
  if (!d.configuration) await d.selectConfiguration(1);
  let target;
  for (const iface of d.configuration.interfaces) {
    for (const alt of iface.alternates) {
      const ep = alt.endpoints.find((e) => e.direction === "out" && e.type === "bulk");
      if (ep && (!target || alt.interfaceClass === 7)) target = { iface, alt, ep };
    }
  }
  if (!target) throw new Error("No printer endpoint");
  if (!target.iface.claimed) await d.claimInterface(target.iface.interfaceNumber);
  if (target.iface.alternates.length > 1) await d.selectAlternateInterface(target.iface.interfaceNumber, target.alt.alternateSetting);
  for (let i = 0; i < bytes.length; i += 4096) await d.transferOut(target.ep.endpointNumber, bytes.slice(i, i + 4096));
}

// ---------- Bluetooth ----------
export async function connectBluetooth() {
  const device = await navigator.bluetooth.requestDevice({ acceptAllDevices: true, optionalServices: BT_SERVICES });
  live.bt = device;
  live.btChar = null;
  return { id: device.id, name: device.name || "Bluetooth printer" };
}

async function btCharacteristic(info) {
  if (!live.bt && navigator.bluetooth.getDevices) {
    const devices = await navigator.bluetooth.getDevices();
    live.bt = devices.find((d) => d.id === info?.id) || null;
  }
  if (!live.bt) throw new Error("Bluetooth printer not paired");
  if (live.btChar && live.bt.gatt.connected) return live.btChar;
  const server = await live.bt.gatt.connect();
  for (const uuid of BT_SERVICES) {
    let svc;
    try {
      svc = await server.getPrimaryService(uuid);
    } catch {
      continue;
    }
    const chars = await svc.getCharacteristics();
    const c = chars.find((x) => x.properties.writeWithoutResponse) || chars.find((x) => x.properties.write);
    if (c) {
      live.btChar = c;
      return c;
    }
  }
  throw new Error("No writable printer characteristic");
}

async function btSend(info, bytes) {
  const c = await btCharacteristic(info);
  const noResp = c.properties.writeWithoutResponse && c.writeValueWithoutResponse;
  for (let i = 0; i < bytes.length; i += 100) {
    const chunk = bytes.slice(i, i + 100);
    if (noResp) await c.writeValueWithoutResponse(chunk);
    else await c.writeValue(chunk);
    await new Promise((r) => setTimeout(r, 12));
  }
}

const withTimeout = (p, ms) => Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), ms))]);

export async function sendToPrinter(cfg, bytes) {
  if (cfg.mode === "usb") {
    if (!supports.usb()) throw new Error("unsupported");
    return withTimeout(usbSend(cfg.usb, bytes), 8000);
  }
  if (cfg.mode === "bluetooth") {
    if (!supports.bluetooth()) throw new Error("unsupported");
    return withTimeout(btSend(cfg.bt, bytes), 15000);
  }
  throw new Error("browser mode");
}
